// 카톡·문자·중개 앱 알림을 붙여 넣으면 이름·연락처·주소·평형을 뽑는다 — 사람이 확인하고 저장하므로 확신이 없으면 틀리게 채우지 않고 비운다.

export type PasteField = "name" | "phone" | "site_address" | "area_pyeong";

// label = 「이름:」 같은 라벨 뒤에서 찾음 · pattern = 라벨 없이 모양으로 추정함.
export type PasteConfidence = "label" | "pattern";

// own = 우리 회사 번호·주소(설정·org 행·담당자 번호) · rep = 라벨 없는 대표번호(15xx·16xx·18xx)는 회사 창구 번호라 고객 번호로 채우지 않는다.
export type PasteSkip =
  | { kind: "phone"; value: string; reason: "own" | "rep" }
  | { kind: "address"; value: string; reason: "own" };

const SKIP_TEXT = {
  "phone-own": "우리 회사 번호라 후보에서 뺐습니다",
  "phone-rep": "대표번호(15xx·16xx·18xx)는 회사 상담 창구 번호라 휴대폰 칸에 채우지 않았습니다",
  "address-own": "우리 회사 주소라 현장 주소 후보에서 뺐습니다",
} as const;

export const pasteSkipText = (skip: PasteSkip): string =>
  skip.kind === "address" ? SKIP_TEXT["address-own"] : SKIP_TEXT[`phone-${skip.reason}`];

// 우리 회사 서명을 가려낼 정보 — 회사 전화·담당자 번호·회사 주소(빈 값은 무시한다).
export interface PasteOwnInfo {
  phones: readonly string[];
  addresses: readonly string[];
}

export interface PasteParseResult {
  name?: string;
  phone?: string;
  site_address?: string;
  area_pyeong?: number;
  // 「30평대」 처럼 평형대로만 적었으면 그 앞자리(30) — 평수가 아니라 평형대 선택지 후보라 area_pyeong 에 넣지 않는다.
  area_decade?: number;
  area_m2?: number;
  phones: string[];
  memo: string;
  memo_truncated: boolean;
  confidence: Partial<Record<PasteField, PasteConfidence>>;
  ambiguous: PasteField[];
  // 너무 길어 앞부분(전체 30만 자 · 줄마다 2,000자)에서만 뽑았으면 true — memo 는 따로 memo_truncated.
  input_truncated?: boolean;
  // 찾았지만 휴대폰 칸 후보로 쓰지 않은 번호와 그 이유 — 없으면 뺀다.
  skipped?: PasteSkip[];
}

// 고객 폼 메모 상한(`schema.ts` text(2000) · `customer-form.tsx` max(2000))과 같다 — 공용 상수가 없어 값을 맞춰 둔다.
export const PASTE_MEMO_MAX = 2_000;
const ADDRESS_MAX = 200;
const INPUT_MAX = 300_000;
const LINE_MAX = 2_000;
const NAME_MAX = 50;

interface Line {
  text: string;
  speaker: string | null;
}

import { isComplexAnchor } from "../../lib/apartment-complex";

// ---- 입력 정리 · 대화 내보내기 머리 걷기 ----

const toHalfWidth = (s: string): string =>
  s.replace(/[０-９Ａ-Ｚａ-ｚ：－（）]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));

// 공백 줄기는 한 칸으로 · 같은 글자(숫자 빼고) 4번 이상은 3번으로 줄인다 — 긴 줄기에서 정규식이 되짚지 않게.
const normalize = (raw: string): string =>
  toHalfWidth(raw.replace(/\r\n?/g, "\n").replace(/[\u00a0\u2007\u202f]/g, " ").replace(/[‐‑‒–—―]/g, "-"))
    .replace(/[ \t\f\v\u3000]+/g, " ")
    .replace(/([^\n0-9])\1{3,}/g, "$1$1$1");

const WS = " \t\n\r\f\v\u3000";

// 끝에서 chars 에 든 글자를 걷는다 — `[…]+$` 정규식은 긴 구두점 줄기에서 2제곱으로 느려진다.
const trimEndChars = (s: string, chars: string): string => {
  let end = s.length;
  while (end > 0 && chars.includes(s[end - 1])) end -= 1;
  return s.slice(0, end);
};

const KAKAO_PC = /^\[([^\]]{1,30})\]\s*\[(?:오전|오후)\s*\d{1,2}:\d{2}\]\s?(.*)$/;
const KAKAO_MOBILE = /^\d{4}(?:년\s*\d{1,2}월\s*\d{1,2}일|\.\s*\d{1,2}\.\s*\d{1,2}\.?)\s*(?:오전|오후)?\s*\d{1,2}:\d{2},\s*(.+?)\s:\s?(.*)$/;
const KAKAO_MOBILE_SYSTEM = /^\d{4}(?:년\s*\d{1,2}월\s*\d{1,2}일|\.\s*\d{1,2}\.\s*\d{1,2}\.?)\s*(?:오전|오후)?\s*\d{1,2}:\d{2}(?:,|$)/;
const KAKAO_PARTNER = /^(.+?)\s*님과 카카오톡 대화$/;
// 「--- 2026년 9월 29일 ---」 구분선 — `^-{3,}.*-{3,}$` 은 대시 줄기에서 3제곱이라 앞뒤만 본다.
const isDashRule = (line: string): boolean =>
  /^-+$/.test(line) || (line.length >= 6 && line.startsWith("---") && line.endsWith("---"));
const SYSTEM_LINES = [
  /^\d{4}년\s*\d{1,2}월\s*\d{1,2}일\s*[월화수목금토일]요일$/,
  /^저장한 날짜\s*:/,
  /^\[(?:Web발신|국외발신|국제발신)\]$/,
  /^\(광고\)$/,
];

// 전체는 줄 경계에서 30만 자까지만 본다(메인 스레드에서 돈다).
const capInput = (raw: string): { text: string; cut: boolean } => {
  if (raw.length <= INPUT_MAX) return { text: raw, cut: false };
  const head = raw.slice(0, INPUT_MAX);
  const nl = head.lastIndexOf("\n");
  return { text: nl > 0 ? head.slice(0, nl) : head, cut: true };
};

// 한 줄은 앞 2,000자만 보고 끝에 걸친 낱말은 버린다.
const capLine = (line: string): string => {
  if (line.length <= LINE_MAX) return line;
  const head = line.slice(0, LINE_MAX);
  const sp = head.lastIndexOf(" ");
  return sp > LINE_MAX / 2 ? head.slice(0, sp) : head;
};

const splitLines = (text: string): { lines: Line[]; partner: string | null; cut: boolean } => {
  const lines: Line[] = [];
  let partner: string | null = null;
  let speaker: string | null = null;
  let cut = false;
  for (const rawLine of text.split("\n")) {
    const trimmed = rawLine.trim();
    if (trimmed.length > LINE_MAX) cut = true;
    const line = capLine(trimmed).trim().replace(/^\[(?:Web발신|국외발신|국제발신)\]\s*/, "");
    if (!line) continue;
    const p = line.endsWith("님과 카카오톡 대화") ? KAKAO_PARTNER.exec(line) : null;
    if (p) {
      partner = p[1];
      continue;
    }
    if (isDashRule(line) || SYSTEM_LINES.some((re) => re.test(line))) continue;
    const pc = KAKAO_PC.exec(line) ?? KAKAO_MOBILE.exec(line);
    if (pc) {
      speaker = pc[1].trim();
      if (pc[2].trim()) lines.push({ text: pc[2].trim(), speaker });
      continue;
    }
    if (KAKAO_MOBILE_SYSTEM.test(line)) continue;
    lines.push({ text: line, speaker });
  }
  return { lines, partner, cut };
};

// 줄 번호별 번호 목록 — 줄마다 전체 번호를 거르면 번호 수 × 줄 수로 느려진다.
const byLine = (phones: PhoneHit[]): ((i: number) => PhoneHit[]) => {
  const map = new Map<number, PhoneHit[]>();
  for (const p of phones) {
    const list = map.get(p.lineIndex);
    if (list) list.push(p);
    else map.set(p.lineIndex, [p]);
  }
  return (i) => map.get(i) ?? [];
};

// ---- 전화 ----

// 번호 칸 사이 구분 — 공백·점 한 칸이거나 하이픈 1~3개(앞뒤 한 칸 공백 허용 — 「010--1234」「010 - - 1234」).
const SEP = "(?:\\s?-(?:\\s?-){0,2}\\s?|[\\s.])?";
const PHONE_RE = new RegExp(
  `(?<![\\d])(?:(?:\\+82${SEP}0?|0)1[016789]${SEP}\\d{3,4}${SEP}\\d{4}|\\+82${SEP}(?:\\(0\\)${SEP}|0)?\\(?(?:2|[3-6][1-5]|70|50[2-8]|303)\\)?${SEP}\\d{3,4}${SEP}\\d{4}|\\(?0(?:2|[3-6][1-5]|70|50[2-8]|303)\\)?${SEP}\\d{3,4}${SEP}\\d{4}|1(?:5(?:22|44|66|77|88|99)|6(?:00|44|61|66|68|70|88)|8(?:00|11|33|55|77|99))[\\s.-]\\d{4}(?!\\s*(?:만|천|억|원|평|㎡|m|개|년)))(?![\\d])`,
  "g"
);
const PHONE_LABEL = /(?:연락처|전화번호|전화|휴대폰|핸드폰|휴대전화|폰|번호|HP|H\.P|Mobile|mobile)\s*[:.]?\s*$/;
// 서명·안내문의 회사 번호 라벨 — 고객 라벨이 아니다(같은 종류 안에서 맨 뒤로 민다).
const COMPANY_LABEL = /(?:(?<![A-Za-z])(?:Tel|TEL|tel)|대표\s*(?:전화)?\s*번호|대표\s*전화|고객\s*센터|사무실(?:\s*(?:번호|전화))?)\s*[:.]?\s*$/;
// 「연락처: Tel 010-…」 처럼 고객 라벨 뒤에 붙은 Tel 은 고객 라벨이다(「대표 전화 Tel」 은 아님).
const CUSTOMER_TEL_LABEL =
  /(?<!(?:대표|사무실|센터)\s*(?:전화)?\s*)(?:연락처|전화번호|전화|휴대폰|핸드폰|휴대전화|폰|번호|HP|H\.P|Mobile|mobile)\s*[:.]?\s*(?:Tel|TEL|tel)\s*[:.]?\s*$/;
// 라벨 없는 대표번호 꼴(15xx·16xx·18xx)은 같은 줄에 통화 낱말이 있어야 번호다 — 없으면 치수(「창 1600-2400 으로」)로 본다.
const CALL_CONTEXT = /연락|전화|통화|문의|콜|call|Call|CALL/;
// 「대표 1588-…」「대표님 1588-…」「상담 1644-…」 처럼 창구 낱말이 바로 앞에 붙으면 통화 낱말처럼 본다(꼬리 치수 검사는 그대로).
const REP_LABEL = /(?:대표님?|상담|센터)\s*(?:[:.]\s*)?$/;
const REP_TAIL_PARTICLE = /^\s?(?:으로요|로요|으로|로|에|번(?:으로|이요|입니다)?|입니다|이에요|예요|요)(?![가-힣])/;
// 창구 낱말만 근거일 때 뒤에 붙으면 치수로 보는 조사(「상담 1800-1200 으로 맞춰」) — 통화 낱말이 있는 줄(「1588-1234 로 전화」)엔 쓰지 않는다.
const REP_SIZE_PARTICLE = /^\s?(?:으로|로|에|까지|부터)/;
// 팩스는 전화를 받지 않으므로 고르지 않는다(phones 에는 남긴다).
const FAX_LABEL = /(?:Fax|FAX|fax|팩스)\s*[:.]?\s*$/;
type PhoneKind = "mobile" | "safe" | "line" | "rep";

const phoneDigits = (match: string): string => {
  const d = match.replace(/\D/g, "");
  if (d.startsWith("82")) return d.startsWith("820") ? d.slice(2) : `0${d.slice(2)}`;
  return d;
};

// 띄운 하이픈(「010 - 1234 - 5678」)은 치수·기간(「02 - 1500 - 2000」)과 헷갈려 — 휴대폰은 국번 자릿수(010 은 네 자리)가, 유선은 통화 맥락이 있어야 번호다.
const SPACED_HYPHEN = /\s-|-\s/;
const spacedPhoneOk = (match: string, digits: string, kind: PhoneKind, context: boolean): boolean => {
  const groups = match.split(/\D+/).filter(Boolean);
  const mid = groups[groups.length - 2] ?? "";
  if (groups.length < 3 || groups[groups.length - 1].length !== 4 || mid.length < 3 || mid.length > 4) return false;
  if (kind === "mobile") return !digits.startsWith("010") || mid.length === 4;
  return context;
};

const phoneKind = (d: string): PhoneKind => {
  if (/^01/.test(d)) return "mobile";
  if (/^(?:050\d|0303)/.test(d)) return "safe";
  if (d.startsWith("0")) return "line";
  return "rep";
};

// 자릿수로 끊는다 — 02 는 두 자리 · 050x·0303·대표번호는 네 자리 국번.
const formatPhone = (d: string): string => {
  const head = d.startsWith("02") ? 2 : /^(?:050\d|0303)/.test(d) || !d.startsWith("0") ? 4 : 3;
  if (!d.startsWith("0")) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, head)}-${d.slice(head, d.length - 4)}-${d.slice(-4)}`;
};

interface PhoneHit {
  digits: string;
  formatted: string;
  kind: PhoneKind;
  labeled: boolean;
  company: boolean;
  fax: boolean;
  own: boolean;
  lineIndex: number;
  start: number;
  end: number;
}

const findPhones = (lines: Line[], own: ReadonlySet<string>): PhoneHit[] => {
  const hits: PhoneHit[] = [];
  lines.forEach((line, lineIndex) => {
    const callContext = CALL_CONTEXT.test(line.text);
    for (const m of line.text.matchAll(PHONE_RE)) {
      const digits = phoneDigits(m[0]);
      if (digits.length < 8 || digits.length > 12) continue;
      const start = m.index ?? 0;
      const end = start + m[0].length;
      const head = line.text.slice(Math.max(0, start - 20), start);
      const kind = phoneKind(digits);
      const fax = FAX_LABEL.test(head);
      const customerTel = !fax && CUSTOMER_TEL_LABEL.test(head);
      const company = !fax && !customerTel && COMPANY_LABEL.test(head);
      const labeled = customerTel || (!fax && !company && PHONE_LABEL.test(head));
      if (SPACED_HYPHEN.test(m[0]) && !spacedPhoneOk(m[0], digits, kind, labeled || company || fax || callContext)) continue;
      const tail = line.text.slice(end, end + 8);
      // 라벨 없는 대표번호 꼴은 통화 낱말이 없거나 바로 뒤에 조사 아닌 한글이 오면(「창 1600-2400 사이즈」·단독 줄 「1800-1200」) 버린다.
      const repLabel = !callContext && REP_LABEL.test(head);
      if (kind === "rep" && !labeled && !company && !fax && (!(callContext || repLabel) || (/^\s?[가-힣]/.test(tail) && !REP_TAIL_PARTICLE.test(tail)) || (repLabel && REP_SIZE_PARTICLE.test(tail)))) continue;
      hits.push({ digits, formatted: formatPhone(digits), kind, labeled, company, fax, own: own.has(digits), lineIndex, start, end });
    }
  });
  return hits;
};

const KIND_RANK: Record<PhoneKind, number> = { mobile: 0, safe: 1, line: 2, rep: 3 };

// 대표번호는 고객 라벨(「연락처:」)이 붙었을 때만 고객 번호로 본다.
const skippedRep = (h: PhoneHit): boolean => h.kind === "rep" && !h.labeled;

// 휴대폰 > 안심번호(050x) > 유선 > 대표번호 · 같은 종류 안에서만 고객 라벨 > 라벨 없음 > 회사 라벨 · 남은 번호가 둘 이상이면 비운다.
const pickPhone = (hits: PhoneHit[]): { hit?: PhoneHit; ambiguous: boolean } => {
  const pool = hits.filter((h) => !h.fax && !h.own && !skippedRep(h));
  if (pool.length === 0) return { ambiguous: false };
  const best = Math.min(...pool.map((h) => KIND_RANK[h.kind]));
  const sameKind = pool.filter((h) => KIND_RANK[h.kind] === best);
  const labeled = sameKind.filter((h) => h.labeled);
  const plain = sameKind.filter((h) => !h.company);
  const top = labeled.length > 0 ? labeled : plain.length > 0 ? plain : sameKind;
  const distinct = new Set(top.map((h) => h.digits));
  if (distinct.size > 1) return { ambiguous: true };
  return { hit: top[0], ambiguous: false };
};

// ---- 공통: 서술어·조사 걷기 ----

const hasBatchim = (c: string): boolean => {
  const code = c.charCodeAt(0) - 0xac00;
  return code >= 0 && code <= 11171 && code % 28 !== 0;
};

// 「래미안이에요」 는 받침 뒤라 「이에요」 를, 「자이에요」 는 「에요」 만 걷는다.
const stripCopula = (s: string): string => {
  let t = trimEndChars(s, `${WS}.,!?~^`);
  const m = /(이에요|이예요|이요|이고요|입니다|예요|에요|이구요|요)$/.exec(t);
  if (m) {
    const before = t.slice(0, t.length - m[1].length);
    const last = before.slice(-1);
    if (m[1].startsWith("이") && m[1] !== "입니다" && !hasBatchim(last) && /[가-힣]/.test(last)) {
      t = t.slice(0, t.length - m[1].length + 1);
    } else {
      t = before;
    }
  }
  return trimEndChars(t, `${WS}.,!?~^`).trim();
};

// ---- 이름 ----

const SURNAMES = new Set(
  Array.from(
    "김이박최정강조윤장임한오서신권황안송류유전홍고문양손배백허남심노하곽성차주우구민진지엄채원천방공현함변염여추도소석선설마길연위표명기반라왕금옥육인맹제모탁국어은편용예경봉사부가복태목형피두감음빈동온호범좌팽승간상갈"
  )
);
const COMPOUND_SURNAMES = ["남궁", "황보", "제갈", "선우", "독고", "사공", "서문"];
const TITLES = /(?:과장|부장|차장|대리|사장|실장|팀장|소장|대표|이사|주임|선생|사모|여사|이모|기사|반장|원장|교수|고객|회원|님)$/;
const NAME_STOP = new Set([
  "안녕", "안녕하세요", "감사", "감사합니다", "고객", "고객님", "사장", "사장님", "실장", "소장", "기사", "대표",
  "이름", "성함", "성명", "연락처", "전화", "전화번호", "번호", "주소", "지역", "평수", "평형", "면적", "요청", "요청사항", "문의",
  "견적", "상담", "시공", "공사", "인테리어", "리모델링", "올수리", "부분수리", "전체", "부분", "일정", "예산", "입주", "이사",
  "도배", "장판", "마루", "타일", "욕실", "주방", "방수", "목공", "전기", "조명", "설비", "배관", "도장", "필름", "철거",
  "샷시", "중문", "가구", "신발장", "현관", "현관문", "베란다", "발코니", "확장", "거실", "안방", "작은방", "화장실",
  "사진", "도면", "확인", "부탁", "부탁드립니다", "가능", "문자", "카톡", "오늘", "내일", "모레", "주말", "평일", "오전", "오후",
  "서울", "경기", "인천", "부산", "대구", "대전", "광주", "울산", "세종", "강원", "충북", "충남", "전북", "전남", "경북",
  "경남", "제주", "신축", "구축", "아파트", "빌라", "주택", "상가", "사무실", "오피스텔", "고맙습니다", "수고", "수고하세요",
  "이번", "이번주", "다음", "다음주", "지금", "나중", "정도", "대략", "우선", "먼저", "오늘의집", "숨고", "기타", "메모", "고객센터",
  "남편", "아내", "엄마", "아빠", "부모", "부모님", "어머니", "아버지", "어머님", "아버님", "와이프", "신랑", "신부", "이웃",
  "신혼부부", "신혼", "임차인", "임대인", "세입자", "집주인", "전세", "월세", "자가", "매매",
]);
// 지역 약칭 — 이름 판정에만 쓴다(「잠실 101동 1203호」 의 단지·동네 접두어는 살린다).
const REGION_NICK = new Set([
  "강남", "강동", "강서", "강북", "서초", "송파", "마포", "용산", "성동", "성북", "동작", "관악", "구로", "금천", "노원",
  "도봉", "은평", "서대문", "동대문", "중랑", "광진", "양천", "영등포", "종로", "성수", "잠실", "목동", "분당", "판교",
  "일산", "수원", "성남", "용인", "고양", "부천", "안양", "안산", "하남", "광명", "평택", "김포", "파주", "화성", "동탄",
  "위례", "광교", "송도", "청라", "해운대", "수영", "기장", "남양주", "구리", "의정부", "시흥", "군포", "의왕", "과천",
]);
// 공종·부위 낱말 — 「천장도」「문틀도」 처럼 조사가 붙은 한 줄을 이름으로 잡지 않게 어간과 대조한다.
const WORK_STEM = new Set([
  "천장", "문틀", "창틀", "가벽", "벽지", "벽면", "외벽", "바닥", "타일", "창호", "창문", "몰딩", "주방", "욕실", "욕조", "변기",
  "세면", "도기", "수전", "거실", "안방", "현관", "중문", "방문", "문짝", "도어", "도배", "장판", "마루", "조명", "전기", "설비",
  "배관", "난방", "수도", "도장", "필름", "철거", "샷시", "샤시", "새시", "가구", "싱크", "방수", "목공", "조적", "단열", "마감",
  "청소", "보양", "줄눈", "선반", "수납", "계단",
]);
const MASKED_NAME = /^[가-힣][*＊○●OoXx◯]{1,3}[가-힣]?$/;

// 한글 2~4자 · 성씨로 시작 · 호칭·흔한 낱말이 아님 · 라벨 없이 찾은 것은 행정구역처럼 끝나는 말(동·구·시…)도 뺀다.
const validName = (raw: string, strict: boolean): string | null => {
  const s = raw.replace(/\s*(?:고객님|고객|님|씨)$/, "").trim();
  if (!/^[가-힣]{2,4}$/.test(s)) return null;
  if (NAME_STOP.has(s) || REGION_NICK.has(s) || TITLES.test(s)) return null;
  const compound = COMPOUND_SURNAMES.some((c) => s.startsWith(c) && s.length >= 3);
  if (!compound && !SURNAMES.has(s[0])) return null;
  if (strict && /(?:동|구|시|군|읍|면|리|로|길|층|평|점|역|쪽|집|방|요|다|까|죠|네|데|니|음|함|됨|줘|해|게)$/.test(s)) return null;
  return s;
};

// 쌍점 라벨은 값이 못 믿을 것이어도 「라벨이 있었다」 로 치고 · 띄어쓰기 라벨(「성함 김민수」)은 값이 이름 모양일 때만 친다.
const NAME_LABEL = /(?:^|[\s\[(])(?:이름|성함|성명|고객명|고객\s*성함|신청자명?|의뢰인|예약자명?|문의자|고객)\s*:\s*(.+)$/;
const NAME_LABEL_SPACE = /(?:^|[\s\[(])(?:이름|성함|성명|고객명|신청자명?|의뢰인|예약자명?)\s+([가-힣].*)$/;

interface NameCandidates {
  label: string | null;
  labelRejected: boolean;
  tier1: string[];
  tier2: string[];
  tier2Particle: Set<string>;
}

const nameFromLabel = (text: string, linePhones: PhoneHit[]): { value: string | null; found: boolean } => {
  const colon = NAME_LABEL.exec(text);
  const m = colon ?? NAME_LABEL_SPACE.exec(text);
  if (!m) return { value: null, found: false };
  let v = m[1];
  const onLine = [...linePhones].sort((a, b) => a.start - b.start);
  const offset = text.length - v.length;
  if (onLine.length > 0 && onLine[0].start >= offset) v = v.slice(0, onLine[0].start - offset);
  v = stripCopula(v.split(/[,/|(]/)[0].trim());
  if (!v) return { value: null, found: false };
  if (MASKED_NAME.test(v.replace(/\s*(?:고객님|님)$/, ""))) return { value: null, found: Boolean(colon) };
  const value = validName(v.replace(/\s+(?=고객님|님|씨)/, ""), false);
  return { value, found: Boolean(colon) || value !== null };
};

const collectNames = (lines: Line[], phones: PhoneHit[], partner: string | null): NameCandidates => {
  const out: NameCandidates = { label: null, labelRejected: false, tier1: [], tier2: [], tier2Particle: new Set() };
  const push = (list: string[], v: string | null) => {
    if (v) list.push(v);
  };
  const onLine = byLine(phones);
  lines.forEach((line, i) => {
    const byLabel = nameFromLabel(line.text, onLine(i));
    if (byLabel.found) {
      if (byLabel.value && !out.label) out.label = byLabel.value;
      else if (!byLabel.value) out.labelRejected = true;
      return;
    }
    const intro = /(?:^|안녕하세요[,.!~\s]*|저는\s*|제\s*이름은\s*|이름은\s*)([가-힣]{2,4})(?:입니다|이에요|예요|이라고\s*합니다|라고\s*합니다)/.exec(line.text);
    if (intro) push(out.tier1, validName(intro[1], true));
    const honor = /(?:^|[\s\]])([가-힣]{2,4})\s?고객님/.exec(line.text) ?? /^([가-힣]{2,4})\s?님[,\s]/.exec(line.text);
    if (honor) push(out.tier1, validName(honor[1], true));
    for (const p of onLine(i)) {
      // 번호 앞뒤 30자만 본다 — 이름 꼴은 그보다 짧고 공백은 한 칸으로 접혀 있다.
      const before = /(?:^|[\s,/(])([가-힣]{2,4})(?:\s*(?:고객님|님))?\s*[/,:()-]?\s*$/.exec(line.text.slice(Math.max(0, p.start - 30), p.start));
      if (before) push(out.tier1, validName(before[1], true));
      const after = line.text.length - p.end <= 30 ? /^\s*[/,)-]?\s*([가-힣]{2,4})(?:\s*(?:고객님|님))?\s*$/.exec(line.text.slice(p.end)) : null;
      if (after) push(out.tier1, validName(after[1], true));
    }
    const alone = trimEndChars(line.text.replace(/^[-*•·]\s*/, ""), `${WS}.,!~`).replace(/입니다$/, "");
    // 흔한 낱말·지역·공종에 조사가 붙은 한 줄(「도배만」「김포는」「천장도」)은 이름이 아니다 — 「김하은」「이정만」 은 어간이 사전에 없어 살린다.
    const particleStem = /^([가-힣]{2})(?:만|도|는|은|을|를)$/.exec(alone);
    const stem = particleStem?.[1] ?? "";
    if (/^[가-힣]{3}(?:\s*(?:고객님|님))?$/.test(alone) && !(particleStem && (NAME_STOP.has(stem) || REGION_NICK.has(stem) || WORK_STEM.has(stem)))) {
      const v = validName(alone, true);
      push(out.tier2, v);
      if (v && particleStem) out.tier2Particle.add(v);
    }
  });
  if (partner) push(out.tier1, validName(partner.trim(), true));
  const speakers = new Set(lines.map((l) => l.speaker).filter((s): s is string => Boolean(s)));
  if (speakers.size === 1 && !partner) push(out.tier1, validName(Array.from(speakers)[0], true));
  return out;
};

// 라벨이 있는데 못 믿을 값이면(「김 과장」「김**」) 다른 곳에서 추정하지 않는다 · 추정 후보가 서로 다르면 비운다.
const pickName = (c: NameCandidates): { value?: string; confidence?: PasteConfidence; ambiguous: boolean } => {
  if (c.label) return { value: c.label, confidence: "label", ambiguous: false };
  if (c.labelRejected) return { ambiguous: false };
  for (const tier of [c.tier1, c.tier2]) {
    let distinct = Array.from(new Set(tier));
    // 단독 줄 후보가 여럿이면 조사로 끝나는 것(「이도만」)을 뺀다.
    const strong = tier === c.tier2 && distinct.length > 1 ? distinct.filter((v) => !c.tier2Particle.has(v)) : [];
    if (strong.length > 0) distinct = strong;
    if (distinct.length === 1) return { value: distinct[0], confidence: "pattern", ambiguous: false };
    if (distinct.length > 1) return { ambiguous: true };
  }
  return { ambiguous: false };
};

// ---- 주소 ----

const PROVINCE =
  "(?:서울특별시|서울시|서울|부산광역시|부산|대구광역시|대구|인천광역시|인천|광주광역시|광주|대전광역시|대전|울산광역시|울산|세종특별자치시|세종|경기도|경기|강원특별자치도|강원도|강원|충청북도|충북|충청남도|충남|전북특별자치도|전라북도|전북|전라남도|전남|경상북도|경북|경상남도|경남|제주특별자치도|제주도|제주)";
const PROVINCE_START = new RegExp(`(?:^|[\\s:(\\[])(${PROVINCE})\\s+[가-힣]{1,5}(?:시|군|구)(?=\\s|$)`);
const ADMIN_STOP = new Set(["식구", "혹시", "역시", "몇시", "오후시", "오전시", "다시", "잠시", "동시", "당시", "즉시", "항시", "수시", "임시", "가구", "친구", "요구", "연구", "도구", "공구", "입구", "출구", "기구", "배수구", "환기구", "점검구"]);
// 실제 시·군·구 이름(자치구·일반구 포함) — 「제시」「문구」「김정시」 같은 낱말을 구로 잡지 않게 이 목록에 있을 때만 주소 시작으로 본다.
const SIGUNGU = new Set([
  "종로구", "중구", "용산구", "성동구", "광진구", "동대문구", "중랑구", "성북구", "강북구", "도봉구", "노원구", "은평구", "서대문구", "마포구", "양천구", "강서구",
  "구로구", "금천구", "영등포구", "동작구", "관악구", "서초구", "강남구", "송파구", "강동구", "서구", "동구", "영도구", "부산진구", "동래구", "남구", "북구",
  "해운대구", "사하구", "금정구", "연제구", "수영구", "사상구", "기장군", "수성구", "달서구", "달성군", "군위군", "미추홀구", "연수구", "남동구", "부평구", "계양구",
  "강화군", "옹진군", "제물포구", "영종구", "검단구", "광산구", "유성구", "대덕구", "울주군", "세종시", "수원시", "성남시", "의정부시", "안양시", "부천시", "광명시",
  "평택시", "동두천시", "안산시", "고양시", "과천시", "구리시", "남양주시", "오산시", "시흥시", "군포시", "의왕시", "하남시", "용인시", "파주시", "이천시", "안성시",
  "김포시", "화성시", "광주시", "양주시", "포천시", "여주시", "연천군", "가평군", "양평군", "장안구", "권선구", "팔달구", "영통구", "수정구", "중원구", "분당구",
  "만안구", "동안구", "원미구", "소사구", "오정구", "상록구", "단원구", "덕양구", "일산동구", "일산서구", "처인구", "기흥구", "수지구", "만세구", "효행구", "병점구",
  "동탄구", "춘천시", "원주시", "강릉시", "동해시", "태백시", "속초시", "삼척시", "홍천군", "횡성군", "영월군", "평창군", "정선군", "철원군", "화천군", "양구군",
  "인제군", "고성군", "양양군", "청주시", "충주시", "제천시", "보은군", "옥천군", "영동군", "증평군", "진천군", "괴산군", "음성군", "단양군", "상당구", "서원구",
  "흥덕구", "청원구", "천안시", "공주시", "보령시", "아산시", "서산시", "논산시", "계룡시", "당진시", "금산군", "부여군", "서천군", "청양군", "홍성군", "예산군",
  "태안군", "동남구", "서북구", "전주시", "군산시", "익산시", "정읍시", "남원시", "김제시", "완주군", "진안군", "무주군", "장수군", "임실군", "순창군", "고창군",
  "부안군", "완산구", "덕진구", "목포시", "여수시", "순천시", "나주시", "광양시", "담양군", "곡성군", "구례군", "고흥군", "보성군", "화순군", "장흥군", "강진군",
  "해남군", "영암군", "무안군", "함평군", "영광군", "장성군", "완도군", "진도군", "신안군", "포항시", "경주시", "김천시", "안동시", "구미시", "영주시", "영천시",
  "상주시", "문경시", "경산시", "의성군", "청송군", "영양군", "영덕군", "청도군", "고령군", "성주군", "칠곡군", "예천군", "봉화군", "울진군", "울릉군", "창원시",
  "진주시", "통영시", "사천시", "김해시", "밀양시", "거제시", "양산시", "의령군", "함안군", "창녕군", "남해군", "하동군", "산청군", "함양군", "거창군", "합천군",
  "의창구", "성산구", "마산합포구", "마산회원구", "진해구", "제주시", "서귀포시",
]);
const DISTRICT_START = /(?:^|[\s:(\[])([가-힣]{1,5}(?:시|군|구))(?=\s[가-힣A-Za-z0-9])/g;
// 시·군·구 다음 낱말이 행정구역·도로명·번지면 주소의 시작이다(「식구 4명이고」「제시 해주신」 은 아님).
const DISTRICT_NEXT =
  /^\s(?:[가-힣A-Za-z0-9]{1,15}(?:시|군|구|동|읍|면|리|로|길)(?:\d{1,4}(?:번?길|가|로)|\d{1,5}(?:-\d{1,5})?)?(?=[\s,]|$)|\d{1,5}(?:-\d{1,5})?(?![가-힣\d]))/;
// 동·호 — 숫자에 붙은 「호」(「1203호요」)는 조사가 와도 되고, 띄운 「호」 는 조사·서술(「1203 호입니다」「호에」)만 — 그 밖의 한글(「20 호반써밋」「12 호텔」)은 행정동+번지+낱말이다.
const HO_TAIL = "(?:입니다|이에요|이고요|이고|예요|에요|이요|요|에서|에|이며|인데|이라|라|는|은|이|와|과|로|까지|도|만)";
const UNIT_SRC = `\\d{1,4}\\s*동\\s*\\d{1,5}(?:호|\\s+호(?=${HO_TAIL}?(?![가-힣])))`;
const UNIT = new RegExp(UNIT_SRC);
// 또는 단지명 낱말 1~4개 뒤에 바로 동·호가 오면(「송파구 헬리오시티 101동 1203호」) 시작이다 — 3낱말 이상은 브랜드가 든 단지명(「래미안 대치 팰리스」)만.
const DISTRICT_COMPLEX = new RegExp(`^\\s(?:[가-힣A-Za-z0-9]{1,15}\\s){1,4}${UNIT_SRC}`);
const COMPLEX_WORD_END = /(?:은|는|(?<!마)을|를|에|고|요|다|께|서)$/;
// 또는 브랜드 단지명이 바로 오면(「연수구 힐스테이트 송도 더 테라스」) 시작이다.
const DISTRICT_BRAND = /^\s([가-힣A-Za-z0-9]{2,15})(?=[\s,]|$)/;
// 단지명 바로 뒤 N단지·동호수(「우리마을 3단지」) — 일반어+마을도 이 자리면 단지명이다.
const UNIT_NEXT = new RegExp(`^\\s+(?:\\d{1,2}\\s*단지|${UNIT_SRC})`);
const ADDRESS_LABEL = /(?:^|[\s\[(])(?:주소|현장\s*주소|시공\s*주소|공사\s*주소|시공지|주소지|현장|위치)\s*[:]\s*(.+)$/;
const ADDRESS_LABEL_LOOSE = /^(?:주소|현장주소|시공주소)\s+(.+)$/;
// 앞 공백은 잡지 않는다(자른 뒤 걷는다) — `\s*` 로 시작하면 긴 공백 줄기에서 2제곱이다.
const ADDRESS_CUT = [
  /[(\[]?\s?\d{1,3}(?:\.\d)?\s?(?:평|py|PY|Py|㎡|m2|m²|제곱)/,
  /[,/]?\s?(?:연락처|전화|휴대폰|핸드폰)/,
  /\|/,
  /(?:(?<![A-Za-z])(?:Tel|TEL|tel|Fax|FAX|fax)(?![A-Za-z])|팩스|대표\s?번호)/,
  /사업자\s?(?:등록)?\s?번호/,
];
// 회사 표지(Tel·대표번호·사업자등록번호)가 든 줄은 회사 서명이라 라벨 없는 주소 후보에서 뺀다 · `|` 만으로는 빼지 않는다(자르기만).
const isSignatureLine = (text: string): boolean =>
  /(?:(?<![A-Za-z])(?:Tel|TEL|tel)(?![A-Za-z])|대표\s?(?:전화)?\s?번호|사업자\s?(?:등록)?\s?번호)/.test(text);
const REGION_TOKEN = new RegExp(`^(?:${PROVINCE}|[가-힣]{1,5}(?:시|군|구))$`);
const PROVINCE_HEAD = new RegExp(`^${PROVINCE}\\s+`);

// 비교용 주소 — 시·도 표기와 공백·기호를 걷되, 공백 자리(cuts)와 숫자 사이 하이픈(「12-3」, 겹친 「12--3」 도 하나로)은 남긴다.
interface AddressKey {
  text: string;
  cuts: ReadonlySet<number>;
}

const addressKey = (s: string): AddressKey => {
  const words = normalize(s)
    .trim()
    .replace(PROVINCE_HEAD, "")
    .replace(/(\d)\s*(?:-\s*)+(?=\d)/g, "$1-")
    .split(/\s+/)
    .map((w) => w.replace(/[^가-힣A-Za-z0-9-]/g, "").replace(/(?<!\d)-|-(?!\d)/g, ""))
    .filter(Boolean);
  const cuts = new Set<number>();
  let text = "";
  for (const word of words) {
    if (text) cuts.add(text.length);
    text += word;
  }
  return { text, cuts };
};

// 번지 글자 — 숫자와 숫자 사이 하이픈.
const numberish = (text: string, i: number): boolean =>
  /\d/.test(text[i] ?? "") || (text[i] === "-" && /\d/.test(text[i - 1] ?? "") && /\d/.test(text[i + 1] ?? ""));

// 번지 글자끼리 맞닿은 자리가 공백 경계가 아니면 한 번지다 — 「123」 은 「1234」·「12」 는 「12-3」 이 아니지만 「123 4층」 에는 든다.
const splitsNumber = (key: AddressKey, at: number): boolean =>
  numberish(key.text, at - 1) && numberish(key.text, at) && !key.cuts.has(at);

// 매치 안쪽 번지 글자 사이의 공백 경계가 한쪽에만 있으면 다른 번지다 — 「123 4층」 과 「1234」.
const innerCutsDiffer = (short: AddressKey, long: AddressKey, at: number): boolean => {
  for (let i = 1; i < short.text.length; i++) {
    if (numberish(short.text, i - 1) && numberish(short.text, i) && short.cuts.has(i) !== long.cuts.has(at + i)) return true;
  }
  return false;
};

// 짧은 쪽(8자 이상)이 긴 쪽에 숫자를 쪼개지 않고 들어 있으면 같은 주소.
const sameAddress = (a: AddressKey, b: AddressKey): boolean => {
  const [short, long] = a.text.length <= b.text.length ? [a, b] : [b, a];
  if (short.text.length < 8) return false;
  for (let at = long.text.indexOf(short.text); at >= 0; at = long.text.indexOf(short.text, at + 1)) {
    if (!splitsNumber(long, at) && !splitsNumber(long, at + short.text.length) && !innerCutsDiffer(short, long, at)) return true;
  }
  return false;
};

// 요청어(「문의」「이사」) — 번지 전이고 뒤에 도로명+번호·면 뒤 리·동리+번지가 이어지면 지명의 앞머리다(「문의면 문의리」「쪽샘길 12」).
const REQUEST_CUT = /\s(?:올수리|전체\s*수리|부분\s*수리|리모델링|인테리어|견적|문의|상담|시공|공사|입주|이사|예정|쪽|근처|사는|살고|거주)/g;
// 번지 뒤 개수·날짜(「3 명」「3/15」「1m」)는 번지가 아니다.
const COUNT_AFTER = "(?![\\d가-힣./~:])(?!\\s*(?:명|월|일|개|곳|군데|번(?!지)|장|m(?![A-Za-z])|cm|mm|/))";
const REQUEST_PLACE = new RegExp(
  `^(?:(?:[가-힣]{0,3}길|[가-힣]{0,2}[가-힣](?<![으비일서간금용])로|[가-힣]{0,3}(?:리|동))\\s*(?:산\\s*)?\\d{1,5}(?:-\\d{1,5})?${COUNT_AFTER}|[가-힣]{0,3}(?:면|읍)\\s+[가-힣]{1,6}(?:리|동|로|길)(?=[\\s\\d]|$))`
);
const requestCutAt = (s: string, end: number): number => {
  const head = s.slice(0, end);
  for (const m of head.matchAll(REQUEST_CUT)) {
    const at = m.index ?? 0;
    if (!HAS_LOT.test(head.slice(0, at)) && REQUEST_PLACE.test(head.slice(at + m[0].length))) continue;
    return at;
  }
  return end;
};

// 공간·공정 낱말(「욕실」「도배만」) — 주소 낱말의 앞머리(「마루빌」)는 자르지 않게 낱말 끝까지 본다.
const TRADE_CUT =
  /\s(?:욕실|화장실|주방|거실|안방|작은방|베란다|발코니|도배|장판|벽지|마루|바닥|타일|샷시|새시|창호|싱크대|중문|몰딩|붙박이장|확장|철거|누수|방수)(?:도|만|을|를|은|는|과|와|이|가|하고|이랑|랑)?(?=[\s,.!?]|$)/g;
// 뒤에 도로명·하이픈 번지·「번지」가 오면 지명이다(「서귀포시 중문 관광로 12」).
const PLACE_AFTER = /^\s+(?:[가-힣A-Za-z0-9]{1,15}(?:로|길)\s*\d|\d{1,5}(?:-\d{1,5})+(?![\d가-힣.])|\d{1,5}(?:-\d{1,5})?\s*번지)/;
// 동리+번지(「중문 색달동 12」) — 街 는 「문래동3가」 처럼 숫자 뒤만(「누수가 3」 의 가는 조사).
const DONG_RI_AFTER = new RegExp(`^\\s+([가-힣][가-힣0-9]{0,14}(?:동|리|\\d가))\\s*(?:산\\s*)?\\d{1,5}(?:-\\d{1,5})?${COUNT_AFTER}`);
// 「동」「리」로 끝나는 공사어(「창호 유리」「주방 이동」)는 지명이 아니다.
const DONG_RI_COMMON = new Set(["이동", "유리", "수리", "처리", "정리", "관리", "거리", "자리", "소리", "요리", "머리", "다리", "무리", "활동", "운동", "작동", "자동", "수동", "연동", "변동", "가동", "행동", "진동"]);
// 앞에 이미 동·리가 있으면(「역삼동 욕실 이동 12」) 뒤 동리는 새 지명이 아니다.
const HAS_DONG_RI = /[가-힣]{2}\d{0,2}(?:동|리)(?:\d{1,2}가)?(?=\s|$)/;
const PLAIN_LOT_AFTER = /^\s+\d{1,5}(?![\d가-힣.-])/;
// 앞에 이미 번지가 있으면(「대치동 12 욕실 2」「테헤란로 12 욕실 2」) 뒤 숫자는 개수다.
const HAS_LOT = /[가-힣A-Za-z0-9](?:로|길)\s*\d|[가-힣]\d{0,2}(?:동|읍|면|리|가)\s*(?:산\s*)?\d/;
const tradeCutAt = (s: string, end: number): number => {
  const head = s.slice(0, end);
  for (const m of head.matchAll(TRADE_CUT)) {
    const at = m.index ?? 0;
    const after = head.slice(at + m[0].length);
    const before = head.slice(0, at);
    // 번지 뒤 낱말은 주소가 아니다(「대치동 12 창호 유리 3 장」).
    if (HAS_LOT.test(before)) return at;
    if (PLACE_AFTER.test(after)) continue;
    const dongRi = DONG_RI_AFTER.exec(after);
    if (dongRi && !DONG_RI_COMMON.has(dongRi[1]) && !HAS_DONG_RI.test(before)) continue;
    if (PLAIN_LOT_AFTER.test(after)) continue;
    return at;
  }
  return end;
};

const cutAddress = (s: string, phones: { start: number }[], offset: number): string => {
  let end = s.length;
  for (const p of phones) if (p.start - offset >= 0 && p.start - offset < end) end = p.start - offset;
  for (const re of ADDRESS_CUT) {
    const m = re.exec(s.slice(0, end));
    if (m && m.index < end) end = m.index;
  }
  end = tradeCutAt(s, requestCutAt(s, end));
  // 오타로 겹친 하이픈(「12--3」)은 번지 하이픈 하나로 본다.
  // 동·호가 주소의 끝이다 — 뒤의 이름·요청은 떼고 괄호 참고항목(「(대치동)」)만 남긴다.
  const unit = UNIT.exec(s.slice(0, end));
  if (unit && !/^\s*\(/.test(s.slice(unit.index + unit[0].length, end))) end = unit.index + unit[0].length;
  const tokens = trimEndChars(stripCopula(s.slice(0, end).replace(/\s+/g, " ").replace(/(\d)\s*(?:-\s*){2,}(?=\d)/g, "$1-").trim()), `${WS},/-(`).split(" ");
  // 「노후아파트」 처럼 단지명이 아닌 「○○아파트」 는 주소가 아니라 설명이다.
  while (
    tokens.length > 1 &&
    (/^[가-힣]+(?:요|다|까|죠|데|고|나|네|서|면)$/.test(tokens[tokens.length - 1]) ||
      (/^[가-힣0-9]+아파트$/.test(tokens[tokens.length - 1]) && !isComplexAnchor(tokens[tokens.length - 1])))
  )
    tokens.pop();
  return tokens.join(" ");
};

// 시·도·구만 있는 것(「경기 성남시 분당구」)은 현장 주소가 아니다.
const regionOnly = (s: string): boolean => s.split(" ").every((t) => REGION_TOKEN.test(t));

// 라벨 없이 주소로 인정하는 모양 — 도로명+번호 · 동/읍/면/리+번지(「대치4동 12」 처럼 숫자 붙은 행정동 포함) · 동호수 · 시·군·구 뒤 브랜드 단지명 중 하나는 있어야 한다.
const strictAddress = (s: string): boolean =>
  /[가-힣A-Za-z0-9](?:로|길)\s*\d/.test(s) ||
  /[가-힣]\d{0,2}(?:동|읍|면|리)\s*(?:산\s*)?\d/.test(s) ||
  UNIT.test(s) ||
  s.split(" ").some((w, k, words) => k > 0 && isComplexAnchor(w, /^\d{1,2}\s*단지/.test(words[k + 1] ?? "")) && !REGION_TOKEN.test(w));

// 단지명 낱말 — 조사·서술어로 끝나지 않고 흔한 낱말이 아니다(1자는 「더」 같은 수식어라 여러 낱말 단지명 안에서만).
const complexWord = (w: string): boolean =>
  /^[가-힣A-Za-z0-9]{1,15}$/.test(w) && !/(?<!마)(?:은|는|을|를|에서|에|요|고|서|다)$/.test(w) && !NAME_STOP.has(w);

const regionStart = (text: string): number | null => {
  const p = PROVINCE_START.exec(text);
  if (p) return p.index + p[0].indexOf(p[1]);
  for (const m of text.matchAll(DISTRICT_START)) {
    if (ADMIN_STOP.has(m[1]) || !SIGUNGU.has(m[1])) continue;
    const at = (m.index ?? 0) + m[0].indexOf(m[1]);
    const rest = text.slice(at + m[1].length, at + m[1].length + 80);
    const complex = DISTRICT_COMPLEX.exec(rest);
    const words = complex ? rest.slice(0, complex[0].search(/\d/)).trim().split(" ") : [];
    const complexOk =
      complex !== null &&
      (words.length <= 2
        ? words.every((w) => w.length >= 2 && !COMPLEX_WORD_END.test(w))
        : words.every(complexWord) && words.some((w, k) => isComplexAnchor(w, k === words.length - 1)));
    const brand = DISTRICT_BRAND.exec(rest);
    if (DISTRICT_NEXT.test(rest) || complexOk || (brand !== null && isComplexAnchor(brand[1], UNIT_NEXT.test(rest.slice(brand[0].length))) && complexWord(brand[1]))) return at;
  }
  return null;
};

const COMPLEX_WORDS_MAX = 4;

// 동호수 앞 단지명 — 브랜드·「○○마을」 낱말부터 동호수까지 이어진 낱말(「래미안 대치 팰리스」)을, 없으면 바로 앞 한 낱말만 붙인다(이름·요청 문장은 브랜드 앞이라 안 먹는다).
const complexBefore = (before: string[]): string[] => {
  let from = -1;
  for (let k = before.length - 1; k >= Math.max(0, before.length - COMPLEX_WORDS_MAX); k -= 1) {
    if (!complexWord(before[k])) break;
    if (isComplexAnchor(before[k], k === before.length - 1 || /^\d{1,2}단지$/.test(before[k + 1]))) from = k;
  }
  if (from >= 0) return before.slice(from);
  const prev = before[before.length - 1] ?? "";
  const keepPrev = /^[가-힣A-Za-z0-9]{2,}$/.test(prev) && !/(?:은|는|을|를|에서|에|요|고|서)$/.test(prev) && !NAME_STOP.has(prev);
  return keepPrev ? [prev] : [];
};

const unitSegment = (text: string): string | null => {
  const m = UNIT.exec(text);
  if (!m) return null;
  const before = text.slice(0, m.index).trim().split(/\s+/).filter(Boolean);
  return stripCopula([...complexBefore(before), m[0].replace(/\s+/g, " ")].join(" "));
};

const isUnitOnlyLine = (text: string): boolean => UNIT.test(text) && regionStart(text) === null;

interface AddressPick {
  value?: string;
  confidence?: PasteConfidence;
  ambiguous: boolean;
  labelRejected: boolean;
  // 우리 회사 주소라 버린 후보.
  ownDropped: string[];
}

const pickAddress = (lines: Line[], phones: PhoneHit[], ownAddresses: readonly AddressKey[]): AddressPick => {
  const onLine = byLine(phones);
  const ownDropped: string[] = [];
  const isOwn = (value: string): boolean => {
    const key = addressKey(value);
    const own = ownAddresses.some((o) => sameAddress(key, o));
    if (own && !ownDropped.includes(value)) ownDropped.push(value);
    return own;
  };
  // 우리 회사 번호가 든 줄은 서명이라 라벨 없는 주소 후보에서 뺀다.
  const ownLines = new Set(phones.filter((p) => p.own).map((p) => p.lineIndex));
  const joined = new Set<number>();
  const withNextUnit = (value: string, i: number): string => {
    const next = lines[i + 1]?.text;
    if (!next || UNIT.test(value) || !isUnitOnlyLine(next) || ADDRESS_LABEL.test(next) || NAME_LABEL.test(next)) return value;
    joined.add(i + 1);
    return `${value} ${cutAddress(next, onLine(i + 1), 0)}`.trim();
  };
  let labelRejected = false;
  for (let i = 0; i < lines.length; i += 1) {
    const text = lines[i].text;
    const m = ADDRESS_LABEL.exec(text) ?? ADDRESS_LABEL_LOOSE.exec(text);
    if (!m) continue;
    const base = cutAddress(m[1], onLine(i), text.length - m[1].length);
    const value = base.split(" ").length >= 2 || REGION_TOKEN.test(base) ? withNextUnit(base, i) : base;
    // 라벨이 붙은 우리 회사 주소(서명의 「주소:」)는 없던 것으로 보고 다음 라벨을 찾는다.
    if (isOwn(value)) continue;
    if (value.split(" ").length >= 2 && !regionOnly(value) && value.length <= ADDRESS_MAX) {
      return { value, confidence: "label", ambiguous: false, labelRejected: false, ownDropped };
    }
    labelRejected = true;
  }
  if (labelRejected) return { ambiguous: false, labelRejected: true, ownDropped };

  joined.clear();
  const found: string[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (joined.has(i) || ownLines.has(i) || isSignatureLine(lines[i].text)) continue;
    const text = lines[i].text;
    const at = regionStart(text);
    if (at !== null) {
      const value = withNextUnit(cutAddress(text.slice(at), onLine(i), at), i);
      if (strictAddress(value) && !regionOnly(value)) {
        found.push(value);
        continue;
      }
      joined.delete(i + 1);
    }
    const unit = unitSegment(text);
    if (unit) found.push(unit);
  }
  const distinct = Array.from(new Set(found)).filter((v) => v.length <= ADDRESS_MAX && !isOwn(v));
  if (distinct.length === 1) return { value: distinct[0], confidence: "pattern", ambiguous: false, labelRejected: false, ownDropped };
  return { ambiguous: distinct.length > 1, labelRejected: false, ownDropped };
};

// ---- 평형 ----

const PYEONG_RE = /(?<![\d.])(\d{1,3}(?:\.\d)?)\s*(?:평형|평(?!대|택|창|균|일|가|소|면|화|생|범|안)|py(?![a-zA-Z])|PY(?![a-zA-Z])|Py(?![a-zA-Z]))/g;
const PYEONG_LABEL = /(?:평수|평형)\s*[:]?\s*(\d{1,3}(?:\.\d)?)(?!\s*(?:㎡|m2|m²|제곱|\d))/g;
const M2_RE = /(?<![\d.])(\d{2,3}(?:\.\d{1,2})?)\s*(?:㎡|m2|m²|M2|제곱미터|제곱)/g;
const PYEONG_BAND_RE = /(?<![\d.])([1-9])0\s*평대/g;
const PYEONG_MIN = 7;
const PYEONG_MAX = 200;

// ㎡ 는 평으로 바꾸지 않는다 — 공급·전용이 적혀 있지 않으면 84㎡ 가 25평인지 34평인지 모른다(area_m2 로만 돌려준다).
const pickArea = (text: string): { pyeong?: number; decade?: number; m2?: number; ambiguous: boolean } => {
  const values = new Set<number>();
  for (const re of [PYEONG_RE, PYEONG_LABEL]) {
    for (const m of text.matchAll(re)) {
      const n = Number(m[1]);
      if (n >= PYEONG_MIN && n <= PYEONG_MAX) values.add(n);
    }
  }
  const m2s = new Set(Array.from(text.matchAll(M2_RE), (m) => Number(m[1])).filter((n) => n >= 15 && n <= 660));
  const m2 = m2s.size === 1 ? Array.from(m2s)[0] : undefined;
  if (values.size === 1) return { pyeong: Array.from(values)[0], m2, ambiguous: false };
  if (values.size > 1) return { m2, ambiguous: true };
  // 평수가 없을 때만 「30평대」 를 평형대 후보로 — 서로 다른 평형대가 둘 이상이면 고르지 않는다.
  const decades = new Set(Array.from(text.matchAll(PYEONG_BAND_RE), (m) => Number(m[1]) * 10));
  return { m2, decade: decades.size === 1 ? Array.from(decades)[0] : undefined, ambiguous: false };
};

// ---- memo ----

const cutMemo = (text: string): { memo: string; truncated: boolean } => {
  if (text.length <= PASTE_MEMO_MAX) return { memo: text, truncated: false };
  let memo = text.slice(0, PASTE_MEMO_MAX);
  if (/[\ud800-\udbff]$/.test(memo)) memo = memo.slice(0, -1);
  return { memo, truncated: true };
};

// ---- 진입점 ----

const NO_OWN: PasteOwnInfo = { phones: [], addresses: [] };

// 설정의 회사 전화·주소와 org 행 값을 둘 다, 멤버 번호까지 모은다 — 빈 값·중복만 빼고 짧은 번호는 파서가 거른다.
export const pasteOwnInfoOf = (input: {
  companyTel: string | null;
  companyAddress: string | null;
  org: { tel: string; address: string } | null;
  memberPhones: readonly (string | null)[];
}): PasteOwnInfo => {
  const clean = (list: readonly (string | null | undefined)[]): string[] =>
    Array.from(new Set(list.map((v) => (v ?? "").trim()).filter(Boolean)));
  return {
    phones: clean([input.companyTel, input.org?.tel, ...input.memberPhones]),
    addresses: clean([input.companyAddress, input.org?.address]),
  };
};

export const parsePastedInquiry = (raw: string, own: PasteOwnInfo = NO_OWN): PasteParseResult => {
  const input = capInput(raw ?? "");
  const text = normalize(input.text).trim();
  const { memo, truncated } = cutMemo((raw ?? "").replace(/\r\n?/g, "\n").trim());
  const result: PasteParseResult = { phones: [], memo, memo_truncated: truncated, confidence: {}, ambiguous: [] };
  if (!text) return result;

  const { lines, partner, cut } = splitLines(text);
  if (input.cut || cut) result.input_truncated = true;
  const ownPhones = new Set(
    own.phones.map((p) => phoneDigits(toHalfWidth(p))).filter((d) => d.length >= 8 && d.length <= 12)
  );
  const ownAddresses = own.addresses.map(addressKey).filter((k) => k.text.length >= 8);
  const phones = findPhones(lines, ownPhones);
  result.phones = Array.from(new Set(phones.filter((p) => !p.own).map((p) => p.formatted)));
  const skipped: PasteSkip[] = [];
  for (const p of phones) {
    if (p.fax || skipped.some((s) => s.value === p.formatted)) continue;
    if (p.own) skipped.push({ kind: "phone", value: p.formatted, reason: "own" });
    else if (skippedRep(p)) skipped.push({ kind: "phone", value: p.formatted, reason: "rep" });
  }

  const phone = pickPhone(phones);
  if (phone.hit) {
    result.phone = phone.hit.formatted;
    result.confidence.phone = phone.hit.labeled ? "label" : "pattern";
  } else if (phone.ambiguous) result.ambiguous.push("phone");

  // 우리 회사 번호 앞뒤의 이름(담당자 서명)은 고객 이름 후보가 아니다.
  const name = pickName(collectNames(lines, phones.filter((p) => !p.own), partner));
  if (name.value && name.value.length <= NAME_MAX) {
    result.name = name.value;
    result.confidence.name = name.confidence;
  } else if (name.ambiguous) result.ambiguous.push("name");

  const address = pickAddress(lines, phones, ownAddresses);
  // 주소 끝에 찾은 이름이 붙어 왔으면(「… 12 김철수 010-…」) 뗀다.
  const siteAddress =
    address.value && result.name && address.value.endsWith(` ${result.name}`) ? address.value.slice(0, -result.name.length - 1) : address.value;
  if (siteAddress) {
    result.site_address = siteAddress;
    result.confidence.site_address = address.confidence;
  } else if (address.ambiguous) result.ambiguous.push("site_address");
  for (const value of address.ownDropped) skipped.push({ kind: "address", value, reason: "own" });
  if (skipped.length > 0) result.skipped = skipped;

  const area = pickArea(lines.map((l) => l.text).join("\n"));
  if (area.pyeong !== undefined) {
    result.area_pyeong = area.pyeong;
    result.confidence.area_pyeong = /(?:평수|평형)\s?:?\s?\d/.test(text) ? "label" : "pattern";
  } else if (area.ambiguous) result.ambiguous.push("area_pyeong");
  if (area.decade !== undefined) result.area_decade = area.decade;
  if (area.m2 !== undefined) result.area_m2 = area.m2;

  return result;
};
